/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useRef, useState } from "react";
import { optimizeUrl } from "@/lib/img";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from "framer-motion";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import { PremiumBadge, LevelBadge, VerifiedBadge, ReliabilityRow } from "@/components/Badges";
import {
  ArrowLeft, MapPin, Share2, MessageCircle, Sparkles, Quote, Award,
  Plane, CheckCircle2, Camera, Briefcase, ChevronDown, ChevronRight,
  Star, X, ArrowUpRight, Trophy, Calendar, Building2, Crown, Plus, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import ConnectButton from "@/components/ConnectButton";

/* =====================================================================
 * Helencia Editorial Portfolio — premium fashion magazine experience.
 * Public talent profile, designed to feel like a mini personal site.
 * Palette : Caramel #C68E59 · Charbon #1A1A1A · Crème #FAF3EA.
 * ===================================================================== */

// Build a WebP srcset for a storage path served via /api/img/{path}?w=...
const IMG_WIDTHS = [320, 480, 640, 800, 1200, 1600, 2000];
const imgUrl = (path, w = 1200) => `${API}/img/${path}?w=${w}`;
const imgSrcSet = (path) => IMG_WIDTHS.map((w) => `${imgUrl(path, w)} ${w}w`).join(", ");

const PRESETS = {
  caramel: { accent: "#C68E59", bg: "#FAF3EA", dark: "#1A1A1A", soft: "#E6C29A" },
  noir:    { accent: "#E6C29A", bg: "#0F0F0F", dark: "#000000", soft: "#FAF3EA" },
  rose:    { accent: "#B86F6F", bg: "#FBEFEC", dark: "#2A1717", soft: "#E8C8C0" },
  ivoire:  { accent: "#A26F3E", bg: "#F8F4EA", dark: "#23211B", soft: "#D9C9A8" },
  olive:   { accent: "#7C8B5E", bg: "#F2EFE3", dark: "#1F2317", soft: "#C8D2A0" },
};

function resolveTheme(pp) {
  const presetKey = pp?.theme_preset || "caramel";
  const base = PRESETS[presetKey] || PRESETS.caramel;
  return {
    accent: pp?.theme_accent || base.accent,
    bg:     pp?.theme_bg     || base.bg,
    dark:   pp?.theme_dark   || base.dark,
    soft:   base.soft,
    fontSerif: pp?.theme_font_serif || "Cormorant Garamond",
  };
}

// Restore default order for fresh display
const SECTIONS_DEFAULT = ["univers", "portfolio", "profil", "collaborations", "helencia"];

const PORTFOLIO_NAV = [
  { id: "portfolio",      label: "Portfolio" },
  { id: "univers",        label: "Univers" },
  { id: "collaborations", label: "Collaborations" },
  { id: "helencia",       label: "Helencia" },
  { id: "contact",        label: "Contact" },
];

/* ---------- Reusable bits ---------- */

const easeLux = [0.25, 0.1, 0.25, 1];

function Reveal({ children, delay = 0, y = 30, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay, ease: easeLux }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function Eyebrow({ children, color = "#C68E59" }) {
  return (
    <p
      className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase font-medium"
      style={{ color }}
    >
      {children}
    </p>
  );
}

function SectionTitle({ eyebrow, title, subtitle, dark = false }) {
  return (
    <div className="mb-12 sm:mb-16">
      <Reveal>
        <Eyebrow color={dark ? "#E6C29A" : "#A26F3E"}>{eyebrow}</Eyebrow>
      </Reveal>
      <Reveal delay={0.08}>
        <h2
          className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight mt-3"
          style={{ color: dark ? "#FFFFFF" : "#1A1A1A" }}
        >
          {title}
        </h2>
      </Reveal>
      {subtitle && (
        <Reveal delay={0.16}>
          <p
            className="mt-4 max-w-2xl text-sm sm:text-base leading-relaxed"
            style={{ color: dark ? "rgba(255,255,255,0.65)" : "#666" }}
          >
            {subtitle}
          </p>
        </Reveal>
      )}
    </div>
  );
}

/* =====================================================================
 * 1. PORTFOLIO NAV — sticky transparent with hover underline.
 * ===================================================================== */
function PortfolioNav({ talentName, onContact, onBack, theme }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [availableIds, setAvailableIds] = useState(new Set());
  const navHeightRef = useRef(0);
  const isScrollingRef = useRef(false);

  // Track scroll for the glassmorphism transition
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Measure nav height so we can offset the smooth scroll target
  useEffect(() => {
    const measure = () => {
      navHeightRef.current = window.matchMedia("(min-width: 640px)").matches ? 80 : 64;
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Body scroll lock while mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  // Detect which nav targets actually exist in DOM (some sections render null when empty).
  // Re-poll after a small delay because sections mount asynchronously.
  useEffect(() => {
    const poll = () => {
      const ids = new Set();
      PORTFOLIO_NAV.forEach((item) => {
        if (document.getElementById(item.id)) ids.add(item.id);
      });
      setAvailableIds((prev) => {
        // shallow compare to avoid noisy re-renders
        if (prev.size === ids.size && [...ids].every((x) => prev.has(x))) return prev;
        return ids;
      });
    };
    poll();
    const t1 = setTimeout(poll, 200);
    const t2 = setTimeout(poll, 800);
    const t3 = setTimeout(poll, 1800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  // Active section tracking — IntersectionObserver across PORTFOLIO_NAV ids that exist in DOM
  useEffect(() => {
    if (availableIds.size === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (isScrollingRef.current) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      {
        rootMargin: `-${navHeightRef.current + 20}px 0px -55% 0px`,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );
    availableIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [availableIds]);

  const navItems = PORTFOLIO_NAV.filter((it) => availableIds.has(it.id));

  /**
   * Premium smooth scroll: manual rAF-based with easeInOutCubic.
   * - Reliable on iOS Safari (CSS smooth-scroll has known glitches).
   * - Respects the sticky nav offset.
   * - Suppresses double-clicks while a scroll is running.
   */
  const scrollTo = (id) => {
    if (isScrollingRef.current) return;
    const el = document.getElementById(id);
    if (!el) return;
    setMobileOpen(false);
    // Defer 60ms so the drawer close animation can start before scrolling
    setTimeout(() => {
      isScrollingRef.current = true;
      setActiveId(id);
      const target = Math.max(0, el.getBoundingClientRect().top + window.scrollY - navHeightRef.current - 8);
      const start = window.scrollY;
      const distance = target - start;
      const duration = Math.min(1100, Math.max(450, Math.abs(distance) * 0.55));
      const t0 = performance.now();
      const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      const step = (now) => {
        const elapsed = now - t0;
        const t = Math.min(1, elapsed / duration);
        window.scrollTo(0, start + distance * easeInOutCubic(t));
        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          isScrollingRef.current = false;
        }
      };
      requestAnimationFrame(step);
    }, 60);
  };

  return (
    <motion.nav
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: easeLux }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="portfolio-nav"
      style={{
        background: scrolled ? "rgba(250,243,234,0.85)" : "transparent",
        backdropFilter: scrolled ? "blur(18px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(234,221,201,0.6)" : "1px solid transparent",
        transition: "background 0.4s ease, border-color 0.4s ease",
      }}
    >
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
        {/* Brand / back */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 group"
          data-testid="portfolio-nav-back"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" style={{ color: scrolled ? "#1A1A1A" : "#FAF3EA" }} />
          <span
            className="font-serif text-base sm:text-lg italic tracking-wide"
            style={{ color: scrolled ? "#1A1A1A" : "#FAF3EA" }}
          >
            {talentName || "Helencia"}
          </span>
        </button>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-9">
          {navItems.map((item) => {
            const isActive = activeId === item.id;
            return (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="relative text-xs tracking-[0.22em] uppercase font-medium transition-opacity duration-300 group"
                style={{
                  color: scrolled ? "#1A1A1A" : "#FAF3EA",
                  opacity: isActive ? 1 : 0.65,
                }}
                data-testid={`portfolio-nav-${item.id}`}
              >
                {item.label}
                <span
                  className="absolute -bottom-2 left-0 right-0 h-px transition-transform duration-500 origin-left"
                  style={{
                    background: scrolled ? (theme?.accent || "#C68E59") : "#E6C29A",
                    transform: isActive ? "scaleX(1)" : "scaleX(0)",
                  }}
                />
              </button>
            );
          })}
        </div>

        <div className="hidden md:block">
          <button
            onClick={onContact}
            className="rounded-full px-5 h-10 text-xs tracking-[0.22em] uppercase font-medium transition-all hover:tracking-[0.28em]"
            style={{
              background: scrolled ? "#1A1A1A" : "#FAF3EA",
              color: scrolled ? "#FAF3EA" : "#1A1A1A",
            }}
            data-testid="portfolio-nav-contact"
          >
            Contact
          </button>
        </div>

        {/* Mobile burger */}
        <button
          type="button"
          className="md:hidden p-2 -mr-2"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
          aria-expanded={mobileOpen}
          data-testid="portfolio-nav-burger"
        >
          <div className="space-y-1.5">
            <span className="block w-6 h-px transition-all duration-300" style={{ background: scrolled || mobileOpen ? "#1A1A1A" : "#FAF3EA", transform: mobileOpen ? "rotate(45deg) translateY(6px)" : "none" }} />
            <span className="block w-6 h-px transition-opacity duration-300" style={{ background: scrolled || mobileOpen ? "#1A1A1A" : "#FAF3EA", opacity: mobileOpen ? 0 : 1 }} />
            <span className="block w-6 h-px transition-all duration-300" style={{ background: scrolled || mobileOpen ? "#1A1A1A" : "#FAF3EA", transform: mobileOpen ? "rotate(-45deg) translateY(-6px)" : "none" }} />
          </div>
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: easeLux }}
            className="md:hidden overflow-hidden border-t border-[#EADDC9] bg-[#FAF3EA]"
            data-testid="portfolio-mobile-drawer"
          >
            <div className="px-6 py-8 space-y-1">
              {navItems.map((item, i) => {
                const isActive = activeId === item.id;
                return (
                  <motion.button
                    key={item.id}
                    type="button"
                    onClick={() => scrollTo(item.id)}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.06, duration: 0.4, ease: easeLux }}
                    className="group block w-full text-left py-3 transition-colors"
                    data-testid={`portfolio-mobile-link-${item.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className="text-base tracking-[0.22em] uppercase font-medium"
                        style={{
                          color: "#1A1A1A",
                          opacity: isActive ? 1 : 0.7,
                          transition: "opacity 0.3s ease",
                        }}
                      >
                        {item.label}
                      </span>
                      <ChevronRight
                        className="w-4 h-4 transition-all duration-300 group-active:translate-x-1"
                        style={{
                          color: isActive ? (theme?.accent || "#C68E59") : "#A26F3E",
                          opacity: isActive ? 1 : 0.5,
                        }}
                      />
                    </div>
                    {isActive && (
                      <motion.span
                        layoutId="mobile-active-bar"
                        className="block h-px mt-2"
                        style={{ background: theme?.accent || "#C68E59", width: "32px" }}
                      />
                    )}
                  </motion.button>
                );
              })}
              <motion.button
                type="button"
                onClick={() => { onContact(); setMobileOpen(false); }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 + PORTFOLIO_NAV.length * 0.06 + 0.1, duration: 0.5, ease: easeLux }}
                className="w-full rounded-full bg-[#1A1A1A] text-white text-sm tracking-[0.22em] uppercase py-4 mt-6 active:bg-[#3A3A3A] transition-colors"
                data-testid="portfolio-mobile-contact-cta"
              >
                Travailler ensemble
              </motion.button>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 + PORTFOLIO_NAV.length * 0.06 + 0.2, duration: 0.5, ease: easeLux }}
                className="mt-8 pt-6 border-t border-[#EADDC9]"
              >
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#A26F3E] mb-3">Plateforme</p>
                <Link
                  to="/talents"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-between py-2 group"
                  data-testid="portfolio-mobile-explore-helencia"
                >
                  <span className="font-serif text-xl italic text-[#1A1A1A]">Explorer Helencia</span>
                  <ArrowUpRight className="w-4 h-4 text-[#A26F3E] transition-transform group-active:translate-x-0.5 group-active:-translate-y-0.5" />
                </Link>
                <p className="text-xs text-[#8B8680] mt-2 leading-relaxed">
                  Découvrez tous les talents, concours et événements de l'écosystème.
                </p>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}

/* =====================================================================
 * 2. EDITORIAL HERO — full-screen image, giant name, location row.
 * ===================================================================== */
function EditorialHero({ data, mainSrc, mainPhoto, theme }) {
  const { user, profile, portfolio_profile: pp } = data;
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y = useSpring(useTransform(scrollY, [0, 600], [0, 120]), { stiffness: 80, damping: 20, mass: 0.6 });
  const scale = useTransform(scrollY, [0, 600], [1.04, 1.14]);
  const fade = useTransform(scrollY, [0, 400], [1, 0.35]);

  const displayName = user?.name || "Talent";
  const firstLine = (displayName || "").split(" ")[0]?.toUpperCase() || "TALENT";
  const restName = (displayName || "").split(" ").slice(1).join(" ").toUpperCase();
  const category = pp?.talent_category || profile?.talent_type || "Talent Helencia";
  const location = profile?.location;

  return (
    <section
      id="hero"
      ref={ref}
      className="relative w-full overflow-hidden text-white"
      data-testid="editorial-hero"
      style={{ height: "100vh", minHeight: "640px", background: theme.dark }}
    >
      {/* Image plein écran avec parallaxe (ou vidéo cover si dispo) */}
      <motion.div style={{ scale }} className="absolute inset-0">
        {pp?.cover_video_url ? (
          <video
            src={pp.cover_video_url}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={mainPhoto ? imgUrl(mainPhoto.storage_path, 1600) : mainSrc}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: "grayscale(0.05) contrast(1.02)" }}
            data-testid="hero-cover-video"
          />
        ) : (mainSrc || mainPhoto) ? (
          <motion.img
            src={mainPhoto ? imgUrl(mainPhoto.storage_path, 1600) : mainSrc}
            srcSet={mainPhoto ? imgSrcSet(mainPhoto.storage_path) : undefined}
            sizes="100vw"
            alt={displayName}
            loading="eager"
            fetchpriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              objectPosition: mainPhoto
                ? `${((mainPhoto.focal_x ?? 0.5) * 100).toFixed(2)}% ${((mainPhoto.focal_y ?? 0.5) * 100).toFixed(2)}%`
                : "50% 30%",
              filter: "grayscale(0.05) contrast(1.02)",
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#3a2412] via-[#1A1A1A] to-[#0a0a0a]" />
        )}
        {/* Vignette éditoriale */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/35 via-transparent to-transparent" />
      </motion.div>

      {/* Top metadata strip */}
      <motion.div
        style={{ opacity: fade }}
        className="absolute top-20 sm:top-24 left-0 right-0 px-5 sm:px-8 lg:px-12 z-10 flex items-start justify-between"
      >
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="text-[10px] sm:text-[11px] tracking-[0.35em] uppercase text-[#E6C29A]"
        >
          Portfolio Helencia
          <span className="mx-3 opacity-50">·</span>
          {new Date().getFullYear()}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="flex items-center gap-2 flex-wrap justify-end"
        >
          {profile?.is_premium && <PremiumBadge />}
          {profile?.level && <LevelBadge level={profile.level} />}
        </motion.div>
      </motion.div>

      {/* Giant editorial name */}
      <motion.div
        style={{ y, opacity: fade }}
        className="absolute inset-x-0 bottom-0 px-5 sm:px-8 lg:px-12 pb-12 sm:pb-16 md:pb-20 z-10"
      >
        <div className="max-w-[1500px] mx-auto">
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7, duration: 0.9, ease: easeLux }}
            className="text-[10px] sm:text-[11px] tracking-[0.35em] uppercase mb-4 sm:mb-6"
            style={{ color: theme.soft }}
          >
            {category}
            {location && <><span className="mx-3 opacity-60">—</span>{location}</>}
          </motion.p>

          <h1
            className="font-serif font-light leading-[0.86] tracking-[-0.02em]"
            data-testid="editorial-hero-name"
            style={{ fontSize: "clamp(3.5rem, 14vw, 16rem)" }}
          >
            <motion.span
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 1.1, ease: easeLux }}
              className="block"
            >
              {firstLine}
            </motion.span>
            {restName && (
              <motion.span
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 1.1, ease: easeLux }}
                className="block italic font-extralight"
                style={{ color: theme.soft }}
              >
                {restName}
              </motion.span>
            )}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.9 }}
            className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:items-end justify-between gap-6"
          >
            <div className="max-w-md">
              {pp?.headline && (
                <p className="font-serif text-lg sm:text-xl italic text-white/85 leading-snug">
                  « {pp.headline} »
                </p>
              )}
              {pp?.availability_status && (
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-[11px] tracking-wider uppercase">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#A8E0A8] animate-pulse" />
                  Disponible pour des projets
                </div>
              )}
            </div>
            <div className="hidden sm:flex items-center gap-3 text-[10px] tracking-[0.28em] uppercase opacity-60 animate-bounce" style={{ animationDuration: "2.5s" }}>
              Découvrir <ChevronDown className="w-4 h-4" />
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}

/* =====================================================================
 * 3. UNIVERS — moodboard + colors + tags + signature.
 * ===================================================================== */
function UniverseSection({ data, theme }) {
  const { portfolio_profile: pp } = data;
  if (!pp) return null;

  const hasAny =
    (pp.mood_tags?.length || 0) +
    (pp.inspirations?.length || 0) +
    (pp.favorite_styles?.length || 0) +
    (pp.artistic_references?.length || 0) +
    (pp.favorite_colors?.length || 0) > 0 ||
    pp.signature_quote || pp.bio || pp.personal_statement;
  if (!hasAny) return null;

  return (
    <section
      id="univers"
      className="relative py-24 sm:py-32 md:py-40 px-5 sm:px-8 lg:px-12 overflow-hidden"
      data-testid="editorial-univers"
      style={{ background: theme.bg }}
    >
      {/* Decorative side label */}
      <div className="hidden lg:block absolute right-8 top-32 origin-bottom-right rotate-90 text-[10px] tracking-[0.5em] uppercase text-[#A26F3E]/40">
        001 — Univers
      </div>

      <div className="max-w-[1500px] mx-auto">
        <SectionTitle
          eyebrow="Univers · Mood"
          title={<>Esthétique <em className="italic text-[#A26F3E]">&amp;</em> énergie</>}
          subtitle="Ce qui nourrit le regard, l'âme et chaque projet."
        />

        {/* Magazine 2-column layout */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Editorial quote / bio */}
          <div className="lg:col-span-7 space-y-10">
            {pp.signature_quote && (
              <Reveal>
                <div className="relative pl-10 sm:pl-14">
                  <Quote className="absolute -top-2 left-0 w-7 h-7 text-[#C68E59]/40" />
                  <p
                    className="font-serif text-3xl sm:text-4xl md:text-5xl italic leading-[1.1] tracking-tight text-[#1A1A1A]"
                    data-testid="univers-quote"
                  >
                    {pp.signature_quote}
                  </p>
                </div>
              </Reveal>
            )}
            {(pp.bio || pp.personal_statement) && (
              <Reveal delay={0.1}>
                <div className="text-[#3A3A3A] leading-[1.85] text-base sm:text-lg max-w-xl">
                  <span className="float-left font-serif text-7xl text-[#C68E59] leading-[0.85] mr-3 mt-1">
                    {((pp.bio || pp.personal_statement) || "").trim().charAt(0)}
                  </span>
                  <span>
                    {((pp.bio || pp.personal_statement) || "").trim().slice(1)}
                  </span>
                </div>
              </Reveal>
            )}
          </div>

          {/* Mood tags / colors / styles */}
          <div className="lg:col-span-5 space-y-8">
            {pp.mood_tags?.length > 0 && (
              <Reveal delay={0.15}>
                <MoodCluster title="Personnalité" items={pp.mood_tags} variant="dark" />
              </Reveal>
            )}
            {pp.favorite_styles?.length > 0 && (
              <Reveal delay={0.2}>
                <MoodCluster title="Styles favoris" items={pp.favorite_styles} variant="cream" />
              </Reveal>
            )}
            {pp.inspirations?.length > 0 && (
              <Reveal delay={0.25}>
                <MoodCluster title="Inspirations" items={pp.inspirations} variant="line" />
              </Reveal>
            )}
            {pp.favorite_colors?.length > 0 && (
              <Reveal delay={0.3}>
                <MoodCluster title="Palette" items={pp.favorite_colors} variant="dot" />
              </Reveal>
            )}
            {pp.artistic_references?.length > 0 && (
              <Reveal delay={0.35}>
                <MoodCluster title="Références" items={pp.artistic_references} variant="line" />
              </Reveal>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function MoodCluster({ title, items, variant = "cream" }) {
  const base = "inline-flex items-center text-xs tracking-wider uppercase font-medium transition-all duration-300";
  const styles = {
    dark:  "bg-[#1A1A1A] text-white px-4 py-2 rounded-full hover:bg-[#3A3A3A]",
    cream: "bg-white text-[#1A1A1A] px-4 py-2 rounded-full border border-[#EADDC9] hover:border-[#C68E59]",
    line:  "text-[#1A1A1A] border-b border-[#1A1A1A]/30 hover:border-[#C68E59] hover:text-[#A26F3E] px-1 py-0.5 rounded-none",
    dot:   "text-[#1A1A1A] gap-2 before:content-[''] before:w-2 before:h-2 before:rounded-full before:bg-[#C68E59]",
  };
  return (
    <div>
      <p className="text-[10px] tracking-[0.32em] uppercase text-[#A26F3E] mb-4 font-medium">{title}</p>
      <div className="flex flex-wrap gap-2">
        {items.map((it) => (
          <span key={it} className={`${base} ${styles[variant]}`}>{it}</span>
        ))}
      </div>
    </div>
  );
}

/* =====================================================================
 * 4. PORTFOLIO MASONRY — premium grid with lightbox.
 * ===================================================================== */
function PortfolioMasonry({ photos, mainPhotoId, theme }) {
  const filtered = useMemo(
    () => photos.filter((p) => p.id !== mainPhotoId && !p.is_main),
    [photos, mainPhotoId]
  );
  const [lightboxIdx, setLightboxIdx] = useState(null);

  if (filtered.length === 0) return null;

  // Editorial varied sizing pattern
  const pattern = ["tall", "regular", "wide", "regular", "tall", "wide", "regular", "tall"];
  const sizeFor = (i) => pattern[i % pattern.length];

  return (
    <section
      id="portfolio"
      className="relative py-24 sm:py-32 md:py-40 px-5 sm:px-8 lg:px-12"
      data-testid="editorial-portfolio"
      style={{ background: theme.dark, color: "#FFFFFF" }}
    >
      <div className="max-w-[1500px] mx-auto">
        <SectionTitle
          eyebrow="Portfolio · Éditions"
          title={<>Campagnes <em className="italic text-[#E6C29A]">&amp;</em> shoots</>}
          subtitle="Chaque image raconte une histoire — éditoriaux, campagnes, collaborations."
          dark
        />

        {/* CSS columns masonry for true editorial flow */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 sm:gap-5 md:gap-6 [column-fill:_balance]">
          {filtered.map((p, i) => {
            const size = sizeFor(i);
            const ratio = size === "tall" ? "aspect-[3/4]" : size === "wide" ? "aspect-[4/3]" : "aspect-[1/1]";
            return (
              <motion.button
                key={p.id}
                onClick={() => setLightboxIdx(i)}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{ duration: 0.9, delay: (i % 6) * 0.05, ease: easeLux }}
                className={`mb-4 sm:mb-5 md:mb-6 block w-full break-inside-avoid group relative overflow-hidden rounded-2xl bg-white/5 ${ratio}`}
                data-testid={`portfolio-tile-${i}`}
              >
                <img
                  src={imgUrl(p.storage_path, 800)}
                  srcSet={imgSrcSet(p.storage_path)}
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  alt={p.title || ""}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-all duration-[1200ms] group-hover:scale-[1.04]"
                  style={{
                    objectPosition: `${((p.focal_x ?? 0.5) * 100).toFixed(2)}% ${((p.focal_y ?? 0.5) * 100).toFixed(2)}%`,
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute inset-x-0 bottom-0 p-5 text-left opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                  {p.category && (
                    <p className="text-[10px] tracking-[0.28em] uppercase text-[#E6C29A]">{p.category}</p>
                  )}
                  {p.title && (
                    <p className="font-serif text-base sm:text-lg text-white mt-1 leading-tight">{p.title}</p>
                  )}
                </div>
                <span className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/0 group-hover:bg-white/95 flex items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100">
                  <ArrowUpRight className="w-3.5 h-3.5 text-[#1A1A1A]" />
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 sm:p-10"
            onClick={() => setLightboxIdx(null)}
            data-testid="portfolio-lightbox"
          >
            <button
              onClick={() => setLightboxIdx(null)}
              className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.4, ease: easeLux }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-5xl max-h-full"
            >
              <img
                src={imgUrl(filtered[lightboxIdx].storage_path, 1600)}
                srcSet={imgSrcSet(filtered[lightboxIdx].storage_path)}
                sizes="100vw"
                alt={filtered[lightboxIdx].title || ""}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
              {(filtered[lightboxIdx].title || filtered[lightboxIdx].category) && (
                <div className="text-center mt-4 text-white">
                  {filtered[lightboxIdx].category && (
                    <p className="text-[10px] tracking-[0.28em] uppercase text-[#E6C29A]">
                      {filtered[lightboxIdx].category}
                    </p>
                  )}
                  {filtered[lightboxIdx].title && (
                    <p className="font-serif text-xl mt-1">{filtered[lightboxIdx].title}</p>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

/* =====================================================================
 * 5. PROFILE INFO — adaptive specs (model / actor / etc.).
 * ===================================================================== */
function ProfileInfoSection({ data, theme }) {
  const { profile, portfolio_profile: pp } = data;
  const role = (profile?.talent_type || "").toLowerCase();
  const isModel = /mannequin|modèle|model/i.test(role) || !role;

  const specs = [];
  if (profile?.height) specs.push({ label: "Taille", value: profile.height });
  if (profile?.measurements) specs.push({ label: "Mensurations", value: profile.measurements });
  if (isModel) {
    if (profile?.shoe_size) specs.push({ label: "Pointure", value: profile.shoe_size });
    if (profile?.hair_color) specs.push({ label: "Cheveux", value: profile.hair_color });
    if (profile?.eye_color) specs.push({ label: "Yeux", value: profile.eye_color });
  }
  if (profile?.experience) specs.push({ label: "Expérience", value: profile.experience });
  if (profile?.location) specs.push({ label: "Basé(e) à", value: profile.location });
  if (profile?.talent_type) specs.push({ label: "Catégorie", value: profile.talent_type });
  if (pp?.languages?.length) specs.push({ label: "Langues", value: pp.languages.join(" · ") });

  if (specs.length === 0) return null;

  return (
    <section
      id="profil"
      className="bg-white py-24 sm:py-32 md:py-40 px-5 sm:px-8 lg:px-12"
      data-testid="editorial-profil"
    >
      <div className="max-w-[1500px] mx-auto">
        <SectionTitle
          eyebrow="Profil · Carte d'identité"
          title={<>Le talent <em className="italic text-[#A26F3E]">en chiffres</em></>}
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-[#EADDC9] border border-[#EADDC9] rounded-3xl overflow-hidden">
          {specs.map((s, i) => (
            <Reveal key={s.label} delay={(i % 6) * 0.05}>
              <div className="bg-white p-6 sm:p-8 h-full flex flex-col justify-between min-h-[160px] hover:bg-[#FAF3EA] transition-colors duration-500" data-testid={`profil-spec-${s.label.toLowerCase()}`}>
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#A26F3E] font-medium">{s.label}</p>
                <p className="font-serif text-2xl sm:text-3xl text-[#1A1A1A] mt-4 leading-tight">{s.value}</p>
              </div>
            </Reveal>
          ))}
        </div>
        {(pp?.willing_to_travel || pp?.preferred_locations?.length || pp?.collaboration_types?.length) && (
          <Reveal delay={0.2}>
            <div className="mt-10 grid sm:grid-cols-2 gap-6">
              {pp?.preferred_locations?.length > 0 && (
                <div className="rounded-3xl bg-[#FAF3EA] p-7">
                  <p className="text-[10px] tracking-[0.28em] uppercase text-[#A26F3E] mb-3">Zones de prédilection</p>
                  <p className="font-serif text-lg text-[#1A1A1A]">{pp.preferred_locations.join(" · ")}</p>
                </div>
              )}
              {pp?.collaboration_types?.length > 0 && (
                <div className="rounded-3xl bg-[#FAF3EA] p-7">
                  <p className="text-[10px] tracking-[0.28em] uppercase text-[#A26F3E] mb-3">Types de projets</p>
                  <p className="font-serif text-lg text-[#1A1A1A]">{pp.collaboration_types.join(" · ")}</p>
                </div>
              )}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}

/* =====================================================================
 * 6. COLLABORATIONS — past projects with brand + date + role.
 * ===================================================================== */
function CollaborationsSection({ data, theme }) {
  const projects = data.projects || [];
  if (projects.length === 0) return null;

  return (
    <section
      id="collaborations"
      className="py-24 sm:py-32 md:py-40 px-5 sm:px-8 lg:px-12"
      data-testid="editorial-collabs"
      style={{ background: theme.bg }}
    >
      <div className="max-w-[1500px] mx-auto">
        <SectionTitle
          eyebrow="Expériences · Crédits"
          title={<>Collaborations <em className="italic text-[#A26F3E]">passées</em></>}
          subtitle="Marques, photographes, événements — sélection éditoriale."
        />
        <div className="space-y-2">
          {projects.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.04}>
              <div className="group border-t border-[#EADDC9] last:border-b py-6 sm:py-7 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 hover:bg-white transition-colors duration-300 px-3 -mx-3 rounded-xl" data-testid={`collab-row-${i}`}>
                <div className="flex-shrink-0 w-20 sm:w-24 text-[10px] tracking-[0.28em] uppercase text-[#A26F3E] font-semibold">
                  {String(i + 1).padStart(2, "0")}
                </div>
                {p.storage_path && (
                  <div className="w-full sm:w-32 h-24 rounded-2xl overflow-hidden bg-[#1A1A1A]/5 flex-shrink-0">
                    <img
                      src={imgUrl(p.storage_path, 480)}
                      srcSet={imgSrcSet(p.storage_path)}
                      sizes="160px"
                      alt={p.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif text-2xl sm:text-3xl text-[#1A1A1A] leading-tight">{p.title}</h3>
                  {(p.role || p.brand || p.photographer) && (
                    <p className="text-sm text-[#666] mt-1.5">
                      {[p.brand, p.role, p.photographer].filter(Boolean).join(" · ")}
                    </p>
                  )}
                </div>
                <div className="hidden sm:flex flex-shrink-0 items-center gap-4 text-[11px] tracking-[0.2em] uppercase text-[#8B8680]">
                  {p.date && <span>{p.date}</span>}
                  {p.location && <span>{p.location}</span>}
                  <ChevronRight className="w-4 h-4 text-[#C68E59] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-500" />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =====================================================================
 * 7. HELENCIA ECOSYSTEM — badges, level, achievements.
 * ===================================================================== */
function HelenciaEcosystem({ data, theme }) {
  const { profile } = data;
  const badges = profile?.badges || [];
  const items = [
    profile?.is_premium && { icon: Crown, label: "Membre Premium", value: "Visibilité étendue · Badge Premium", color: "#E6C29A" },
    profile?.level && { icon: Star, label: "Niveau Helencia", value: profile.level, color: "#C68E59" },
    badges.length > 0 && { icon: Award, label: "Distinctions", value: `${badges.length} badge${badges.length > 1 ? "s" : ""}`, color: "#A26F3E" },
  ].filter(Boolean);
  // Always render the section: even without badges it serves as a discreet
  // gateway to the Helencia ecosystem (about + discover CTA at the bottom).

  return (
    <section
      id="helencia"
      className="bg-white py-24 sm:py-32 md:py-40 px-5 sm:px-8 lg:px-12 relative overflow-hidden"
      data-testid="editorial-helencia"
    >
      {/* Decorative serif H */}
      <div className="pointer-events-none absolute -bottom-32 -right-10 lg:-right-20 font-serif text-[40rem] leading-none text-[#FAF3EA] select-none">H</div>

      <div className="max-w-[1500px] mx-auto relative">
        <SectionTitle
          eyebrow="Écosystème Helencia"
          title={<>Reconnu <em className="italic text-[#A26F3E]">par la maison</em></>}
          subtitle={items.length > 0
            ? "Statut, distinctions et certifications attribuées par Helencia."
            : "Ce portfolio fait partie de l'écosystème Helencia — découvrez la plateforme."
          }
        />

        {items.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
            {items.map((it, i) => (
              <Reveal key={it.label} delay={i * 0.08}>
                <div className="rounded-3xl border border-[#EADDC9] p-7 sm:p-8 h-full hover:shadow-[0_24px_60px_-30px_rgba(110,75,42,0.25)] transition-shadow duration-500">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-5" style={{ background: `${it.color}1f` }}>
                    <it.icon className="w-5 h-5" style={{ color: it.color }} />
                  </div>
                  <p className="text-[10px] tracking-[0.28em] uppercase text-[#A26F3E]">{it.label}</p>
                  <p className="font-serif text-2xl sm:text-3xl text-[#1A1A1A] mt-2 leading-tight">{it.value}</p>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        {badges.length > 0 && (
          <Reveal delay={0.15}>
            <div className="rounded-3xl bg-[#1A1A1A] text-white p-8 sm:p-10">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#E6C29A] mb-5">Badges décernés</p>
              <div className="flex flex-wrap gap-2.5">
                {badges.map((b) => (
                  <span key={b} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-xs tracking-wider uppercase">
                    <Sparkles className="w-3 h-3 text-[#E6C29A]" /> {b}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        )}

        {data.reliability && (data.reliability.review_count > 0 || data.reliability.badges?.length > 0) && (
          <Reveal delay={0.2}>
            <div className="mt-6 rounded-3xl bg-[#FAF3EA] p-7" data-testid="helencia-reliability">
              <ReliabilityRow
                rating={data.reliability.avg_rating || 0}
                count={data.reliability.review_count || 0}
                badges={data.reliability.badges || []}
                max={4}
              />
            </div>
          </Reveal>
        )}

        {/* "À propos de Helencia" — discreet platform gateway */}
        <Reveal delay={0.3}>
          <div
            className="mt-12 sm:mt-16 rounded-3xl border border-[#EADDC9] p-7 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-10"
            data-testid="helencia-about-platform"
          >
            <div className="flex-1">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#A26F3E] mb-3">À propos</p>
              <p className="font-serif text-2xl sm:text-3xl text-[#1A1A1A] leading-tight">
                Helencia connecte les <em className="italic text-[#A26F3E]">talents</em> aux marques, créateurs et photographes.
              </p>
              <p className="mt-4 text-sm sm:text-base text-[#666] leading-relaxed max-w-xl">
                Portfolios éditoriaux, concours, événements et collaborations — l'écosystème dédié à la mode et la beauté.
              </p>
            </div>
            <Link
              to="/talents"
              className="group inline-flex items-center gap-3 rounded-full bg-[#1A1A1A] hover:bg-[#3A3A3A] text-white px-7 h-12 text-xs tracking-[0.22em] uppercase font-medium transition-all flex-shrink-0"
              data-testid="helencia-discover-cta"
            >
              Découvrir les talents
              <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* =====================================================================
 * 8. CONTACT CTA — final magazine-style cover.
 * ===================================================================== */
function ContactSection({ displayName, onCollab, onShare, theme, userId }) {
  return (
    <section
      id="contact"
      className="relative text-white py-28 sm:py-36 md:py-44 px-5 sm:px-8 lg:px-12 overflow-hidden"
      data-testid="editorial-contact"
      style={{ background: theme.dark }}
    >
      <div className="max-w-[1500px] mx-auto text-center relative">
        <Reveal>
          <Eyebrow color="#E6C29A">Créons ensemble</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2
            className="font-serif italic font-light leading-[0.95] mt-6 tracking-tight"
            style={{ fontSize: "clamp(2.5rem, 8vw, 7rem)" }}
          >
            Travaillons<br />ensemble<span className="text-[#E6C29A]">.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 text-base sm:text-lg text-white/65 max-w-xl mx-auto leading-relaxed">
            Une vision, une marque, un projet ? Lancez une collaboration Helencia pour initier le contact avec {displayName?.split(" ")[0] || "ce talent"}.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <Button
              onClick={onCollab}
              className="rounded-full bg-[#C68E59] hover:bg-[#E6C29A] hover:text-[#1A1A1A] text-white px-9 h-14 text-sm tracking-[0.22em] uppercase font-medium group"
              data-testid="editorial-final-collab-btn"
            >
              Proposer un projet
              <ArrowUpRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Button>
            <button
              onClick={onShare}
              className="rounded-full border border-white/30 hover:border-white text-white hover:bg-white/5 px-9 h-14 text-sm tracking-[0.22em] uppercase font-medium transition-all flex items-center"
              data-testid="editorial-final-share-btn"
            >
              <Share2 className="w-4 h-4 mr-2" /> Partager le profil
            </button>
            <ConnectButton userId={userId} variant="dark" className="!h-14 !px-9 !text-sm !tracking-[0.22em]" />
          </div>
        </Reveal>
        <Reveal delay={0.4}>
          <p className="mt-16 text-[10px] tracking-[0.4em] uppercase text-white/30">
            Helencia · Plateforme des talents · Mode &amp; Beauté
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* =====================================================================
 * 9. PLATFORM FOOTER — Helencia ecosystem discovery / gateway.
 * ===================================================================== */
function PlatformFooter({ theme }) {
  return (
    <footer
      className="relative px-5 sm:px-8 lg:px-12 py-16 sm:py-20"
      data-testid="platform-footer"
      style={{ background: theme.bg, borderTop: "1px solid rgba(110,75,42,0.08)" }}
    >
      <div className="max-w-[1500px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Brand + about */}
          <div className="lg:col-span-6">
            <Reveal>
              <p className="text-[10px] tracking-[0.4em] uppercase text-[#A26F3E]">Plateforme</p>
              <p
                className="font-serif text-4xl sm:text-5xl mt-3 text-[#1A1A1A] leading-[0.95]"
                style={{ fontFamily: "var(--pt-font-serif)" }}
              >
                Helencia<span className="text-[#C68E59]">.</span>
              </p>
              <p className="mt-5 text-sm sm:text-base text-[#3A3A3A] leading-relaxed max-w-md">
                Une plateforme dédiée aux talents, créateurs, collaborations, concours et opportunités —
                pensée pour la mode, la beauté et les projets éditoriaux.
              </p>
            </Reveal>
          </div>

          {/* Discovery links */}
          <div className="lg:col-span-3">
            <Reveal delay={0.08}>
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#A26F3E] mb-5">Explorer</p>
              <ul className="space-y-3 text-sm">
                <FooterLink to="/talents" label="Tous les talents" testid="footer-link-talents" />
                <FooterLink to="/concours" label="Concours en cours" testid="footer-link-concours" />
                <FooterLink to="/events" label="Événements" testid="footer-link-events" />
                <FooterLink to="/" label="Plateforme" testid="footer-link-home" />
              </ul>
            </Reveal>
          </div>

          {/* CTA join */}
          <div className="lg:col-span-3">
            <Reveal delay={0.16}>
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#A26F3E] mb-5">Rejoindre</p>
              <Link
                to="/register"
                className="group inline-flex items-center gap-3 rounded-full border border-[#1A1A1A] px-6 h-12 text-xs tracking-[0.22em] uppercase font-medium text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white transition-all duration-500"
                data-testid="footer-cta-join"
              >
                Rejoindre la communauté
                <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <p className="mt-5 text-xs text-[#8B8680] leading-relaxed">
                Créez votre portfolio, candidatez aux collaborations et participez aux concours éditoriaux.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Bottom bar */}
        <Reveal delay={0.25}>
          <div className="mt-14 pt-7 border-t border-[#EADDC9] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#8B8680]">
              Helencia · Plateforme des talents · Mode &amp; Beauté
            </p>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#8B8680]">
              © {new Date().getFullYear()} · {typeof window !== "undefined" ? window.location.hostname : "helencia.fr"}
            </p>
          </div>
        </Reveal>
      </div>
    </footer>
  );
}

function FooterLink({ to, label, testid }) {
  return (
    <li>
      <Link
        to={to}
        className="inline-flex items-center gap-2 text-[#1A1A1A] hover:text-[#A26F3E] transition-colors duration-300 group"
        data-testid={testid}
      >
        <span className="border-b border-transparent group-hover:border-[#C68E59] transition-colors duration-300">{label}</span>
        <ArrowUpRight className="w-3.5 h-3.5 opacity-40 group-hover:opacity-100 transition-opacity" />
      </Link>
    </li>
  );
}

/* =====================================================================
 * MAIN PAGE
 * ===================================================================== */
export default function TalentProfileEditorial() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [collabDialog, setCollabDialog] = useState(null); // null | { loading, collabs }

  useEffect(() => {
    setError(false);
    api.get(`/talents/${id}`)
      .then(({ data }) => {
        setData({
          ...data,
          photos: Array.isArray(data?.photos) ? data.photos : [],
          projects: Array.isArray(data?.projects) ? data.projects : [],
        });
      })
      .catch(() => setError(true));
  }, [id]);

  const mainPhoto = useMemo(() => {
    if (!data) return null;
    return data.photos.find((p) => p.is_main) || data.photos[0] || null;
  }, [data]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF3EA] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <p className="text-[10px] tracking-[0.4em] uppercase text-[#A26F3E]">Talent introuvable</p>
          <h1 className="font-serif text-4xl mt-3 text-[#1A1A1A]">Ce profil n'est pas disponible</h1>
          <Button onClick={() => navigate("/")} className="mt-8 rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white px-7 h-12">
            Retour
          </Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <motion.div
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-[10px] tracking-[0.5em] uppercase text-[#E6C29A]"
        >
          Helencia
        </motion.div>
      </div>
    );
  }

  const { profile, user, photos } = data;
  const pp = data.portfolio_profile || {};
  const theme = resolveTheme(pp);
  const sectionOrder = (Array.isArray(pp.theme_section_order) && pp.theme_section_order.length > 0)
    ? pp.theme_section_order
    : SECTIONS_DEFAULT;
  const mainSrc = optimizeUrl(mainPhoto ? `${API}/files/${mainPhoto.storage_path}` : profile?.demo_photo_url, 1280);
  const displayName = user?.name;

  // SEO — dynamic title, description, OG image, JSON-LD Person
  const talentCategory = pp.talent_category || profile?.talent_type || "Talent";
  const seoTitle = `${displayName || "Talent"} — ${talentCategory}${profile?.location ? ` · ${profile.location}` : ""}`;
  const seoDescription =
    (pp.headline || pp.signature_quote || pp.bio || "")
      .toString().trim().slice(0, 200)
    || `Portfolio Helencia de ${displayName || "ce talent"} — ${talentCategory}. Découvrez son univers, ses collaborations et ses projets.`;
  // Use the dynamic OG card endpoint (Spotify/Vercel-style branded share image).
  const seoImage = typeof window !== "undefined"
    ? `${window.location.origin}/api/og/talents/${id}.png`
    : undefined;
  const profileUrl = typeof window !== "undefined"
    ? `${window.location.origin}/talents/${id}`
    : undefined;
  const jsonLd = displayName
    ? {
        "@context": "https://schema.org",
        "@type": "Person",
        name: displayName,
        jobTitle: talentCategory,
        ...(profile?.location ? { address: { "@type": "PostalAddress", addressLocality: profile.location } } : {}),
        ...(seoImage ? { image: seoImage } : {}),
        ...(profileUrl ? { url: profileUrl } : {}),
        ...(pp.headline ? { description: pp.headline } : {}),
        affiliation: { "@type": "Organization", name: "Helencia" },
      }
    : null;

  const startCollab = async () => {
    if (!currentUser) {
      toast("Connectez-vous pour proposer un projet.");
      navigate("/login");
      return;
    }
    if (currentUser.role !== "creator" && currentUser.role !== "professional") {
      toast("Seuls les créateurs peuvent proposer des collaborations.");
      return;
    }
    // Fetch the brand's own collaborations to see whether they've already
    // published one. We only surface *active* collabs (talents can apply to
    // those) — drafts/closed/completed aren't usable to onboard a new talent.
    setCollabDialog({ loading: true, collabs: [] });
    try {
      const { data } = await api.get("/my/collaborations");
      const active = Array.isArray(data)
        ? data.filter((c) => c.status === "active" || c.status === "open")
        : [];
      if (active.length === 0) {
        // First-time brand → keep the original friendly onboarding.
        setCollabDialog(null);
        navigate("/createurs/collaborations?new=1");
        toast("Publiez une première collaboration pour contacter ce talent.");
        return;
      }
      setCollabDialog({ loading: false, collabs: active });
    } catch (e) {
      setCollabDialog(null);
      toast.error("Impossible de récupérer vos collaborations.");
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/talents/${id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${displayName} · Helencia`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Lien copié dans le presse-papiers");
      }
    } catch { /* user cancelled */ }
  };

  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  const SECTION_MAP = {
    univers:        <UniverseSection key="univers" data={data} theme={theme} />,
    portfolio:      <PortfolioMasonry key="portfolio" photos={photos} mainPhotoId={mainPhoto?.id} theme={theme} />,
    profil:         <ProfileInfoSection key="profil" data={data} theme={theme} />,
    collaborations: <CollaborationsSection key="collaborations" data={data} theme={theme} />,
    helencia:       <HelenciaEcosystem key="helencia" data={data} theme={theme} />,
  };

  return (
    <div
      className="antialiased editorial-themed"
      data-testid="talent-profile-editorial"
      data-theme-preset={pp.theme_preset || "caramel"}
      style={{
        background: theme.bg,
        color: theme.dark,
        ["--pt-accent"]: theme.accent,
        ["--pt-bg"]: theme.bg,
        ["--pt-dark"]: theme.dark,
        ["--pt-soft"]: theme.soft,
        ["--pt-font-serif"]: `'${theme.fontSerif}', 'Cormorant Garamond', serif`,
      }}
    >
      <SEO
        title={seoTitle}
        description={seoDescription}
        image={seoImage}
        url={profileUrl}
        type="profile"
        jsonLd={jsonLd}
      />
      {/* Scoped editorial typography override — applies the chosen serif to every .font-serif within. */}
      <style>{`
        .editorial-themed .font-serif,
        .editorial-themed h1,
        .editorial-themed h2,
        .editorial-themed h3 { font-family: var(--pt-font-serif) !important; }
        .editorial-themed [data-themed-accent] { color: var(--pt-accent) !important; }
        .editorial-themed [data-themed-accent-bg] { background-color: var(--pt-accent) !important; }
        /* Smooth-scroll anchor offset so sections aren't hidden behind the fixed nav */
        .editorial-themed section[id] { scroll-margin-top: 80px; }
      `}</style>
      <PortfolioNav
        talentName={displayName}
        onContact={scrollToContact}
        onBack={() => navigate(-1)}
        theme={theme}
      />

      <EditorialHero data={data} mainSrc={mainSrc} mainPhoto={mainPhoto} theme={theme} />

      {sectionOrder.map((sid) => SECTION_MAP[sid]).filter(Boolean)}

      <ContactSection
        userId={id}
        displayName={displayName}
        onCollab={startCollab}
        onShare={share}
        theme={theme}
      />

      <PlatformFooter theme={theme} />

      {/* ============================================================
       * Dialog "Sélectionner une collaboration"
       * ============================================================
       * Fixes a UX bug where a brand that had already published one or more
       * active collaborations was still redirected to the "publish new"
       * screen when clicking "Collaborer". We now open a picker with the
       * list of active collabs (talents can apply to those) + a CTA to
       * create a new one if needed.
       * ============================================================ */}
      <AnimatePresence>
        {collabDialog && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onClick={() => setCollabDialog(null)}
            data-testid="collab-picker-overlay"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 26 }}
              className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="collab-picker-dialog"
            >
              <div className="px-6 pt-6 pb-4 border-b border-[#F0E4D2]">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="caption-label text-[#A26F3E]">Contacter {displayName?.split(" ")[0] || "ce talent"}</p>
                    <h3 className="font-serif text-2xl text-[#1A1A1A] mt-1">Sélectionnez une collaboration</h3>
                    <p className="text-sm text-[#6E4B2A] mt-2 leading-relaxed">
                      Voici vos collaborations actives — le talent peut candidater à celles-ci. Vous pouvez aussi en publier une nouvelle, pensée spécialement pour lui.
                    </p>
                  </div>
                  <button
                    onClick={() => setCollabDialog(null)}
                    className="p-2 rounded-full hover:bg-[#FAF3EA] transition"
                    data-testid="collab-picker-close"
                    aria-label="Fermer"
                  >
                    <X className="w-5 h-5 text-[#8C7D6B]" />
                  </button>
                </div>
              </div>

              <div className="max-h-[50vh] overflow-y-auto px-6 py-4">
                {collabDialog.loading ? (
                  <div className="flex items-center justify-center py-10 text-[#8C7D6B]">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
                  </div>
                ) : (
                  <ul className="space-y-3" data-testid="collab-picker-list">
                    {collabDialog.collabs.map((c) => (
                      <li key={c.id}>
                        <button
                          onClick={() => {
                            setCollabDialog(null);
                            navigate(`/collaborations/${c.id}`);
                          }}
                          className="w-full text-left rounded-2xl border border-[#EADDC9] hover:border-[#C68E59] hover:bg-[#FAF3EA] transition p-4 flex items-center gap-3"
                          data-testid={`collab-picker-item-${c.id}`}
                        >
                          <div className="w-10 h-10 rounded-full bg-[#FAF3EA] flex items-center justify-center flex-shrink-0">
                            <Briefcase className="w-5 h-5 text-[#A26F3E]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-[#1A1A1A] truncate">{c.title || "Sans titre"}</p>
                            <p className="text-xs text-[#8C7D6B] mt-0.5">
                              {c.application_count || 0} candidature{(c.application_count || 0) > 1 ? "s" : ""}
                              {c.category ? ` · ${c.category}` : ""}
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-[#8C7D6B] flex-shrink-0" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="px-6 py-4 border-t border-[#F0E4D2] bg-[#FDFAF5]">
                <Button
                  onClick={() => {
                    setCollabDialog(null);
                    navigate("/createurs/collaborations?new=1");
                  }}
                  variant="outline"
                  className="w-full rounded-full border-[#C68E59] text-[#A26F3E] hover:bg-[#FAF3EA] h-11"
                  data-testid="collab-picker-new"
                >
                  <Plus className="w-4 h-4 mr-2" /> Publier une nouvelle collaboration
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
